import React, { type FC, useCallback, useMemo, useRef } from "react";
import { UsersAssignmentModal } from "../../../../../../../../components";
import { flatMap, isEmpty, map } from "lodash";
import { type SortingDirection, Strings } from "../../../../../../../../enums";
import { usersFilter } from "../../../../../../../../components/filterForms/UsersFilterForm/usersFilter";
import userListUtils from "../../../../../../../../utils/userListUtils";
import type { Filters } from "../../../../../../../../utils/queryUtils";
import { fetchGroupUsersAssignment } from "../../../../state/thunks/groupMembersAssignmentThunk";
import { bindActionCreators } from "redux";
import { connect, type ConnectedProps } from "react-redux";
import { type AppDispatch, type RootState } from "../../../../../../../Application/globaltypes/redux";
import { bindAction, type NotifyStepSettings, type User } from "../../../../../../../../interfaces";
import { resetFilter, setSearch } from "../../../../state/slices/groupMembersAssignmentFiltersSlice";
import { selectedItemsChanged, resetSelectedItems } from "../../../../state/slices/groupMembersAssignmentSlice";
import { shouldShowModalForGroupMembership } from "../../../../../../../Licensing/ContentAssignmentModalLicensingSteps/state/thunks/licensingModalThunk";
import { addUsersToGroups } from "../../../../../../state/thunks/peopleThunk";
import { reset } from "../../../../../../../Licensing/ContentAssignmentModalLicensingSteps/state/slices/licensingModalSlice";
import { type GroupAvailableUsers } from "../../../../../../types";
import { setActiveModal } from "../../../../state/slices/groupMembersModalsSlice";
import { useEventCallback } from "../../../../../../../../hooks/useEventCallback";
import { useFeatureFlag } from "../../../../../../../../hooks/useFeatureFlag";
import { FeatureFlags } from "../../../../../../../../featureFlags";

export interface Props extends PropsFromRedux {
  groupId: number;
}

export const UsersAssignmentModalContainer: FC<Props> = (props) => {
  const {
    addUsersToGroupsAction,
    resetFilterAction,
    resetSelectedItemsAction,
    resetLicensingModalAction,
    setActiveModalAction,
    selectedIds,
    groupId,
  } = props;
  const reloadListItems = useRef<(enableSorting: boolean) => void>();
  const additionalPurchaseEnabled = useFeatureFlag(FeatureFlags.AdditionalPurchase);

  const createReloadListItems = (reloadListItemsFunc: (enableSorting: boolean) => void) => {
    reloadListItems.current = reloadListItemsFunc;
  };

  const onSearchMembersChanged = (search: string) => {
    props.setSearchAction(search);
    reloadListItems.current?.(isEmpty(search));
  };

  const loadUsersToAddPage = (
    skip: number,
    top: number,
    sortingColumnName: string,
    sortingDirection: SortingDirection,
    appliedFilter: Filters,
  ) => {
    const filter = props.search ? appliedFilter : usersFilter.buildFilterQuery(appliedFilter);
    let orderByParams = "";
    orderByParams = props.search
      ? userListUtils.formatOrderParamsV2(sortingColumnName, sortingDirection)
      : userListUtils.formatOrderParams(sortingColumnName, sortingDirection);

    props.fetchGroupUsersAssignmentAction({
      groupId: props.groupId,
      skip,
      top,
      orderByParams,
      filter,
      search: props.search,
    });
  };

  const onSecondStep = (selectedIds: number[]) => {
    props.selectedItemsAction(selectedIds);
    return props.shouldShowLicensingModalAction([props.groupId], selectedIds, additionalPurchaseEnabled);
  };

  const onCloseAddUsersModalHandler = useCallback(() => {
    resetFilterAction();
    resetSelectedItemsAction();
    resetLicensingModalAction();
    setActiveModalAction(null);
  }, [resetFilterAction, resetSelectedItemsAction, resetLicensingModalAction, setActiveModalAction]);

  const onAddUsersConfirm = useEventCallback(
    async (notificationSettings?: NotifyStepSettings) => {
      addUsersToGroupsAction(selectedIds, [groupId], notificationSettings);
      onCloseAddUsersModalHandler();
    },
    [onCloseAddUsersModalHandler, addUsersToGroupsAction, selectedIds, groupId],
  );

  const focusedEntityIds = useMemo(() => [props.groupId], [props.groupId]);
  return (
    <UsersAssignmentModal
      showModal
      header="Add Members"
      loadPage={loadUsersToAddPage}
      usersAmount={props.itemsCount}
      isListLoading={props.isLoading}
      isStepLoading={props.isShouldShowModalLoading}
      users={props.items as unknown as User[]}
      onSecondStep={onSecondStep}
      onConfirm={onAddUsersConfirm}
      onCancel={onCloseAddUsersModalHandler}
      assignmentEntityType="group"
      focusedEntityIds={focusedEntityIds}
      usersAssignments={getUsersAssignments(props.items)}
      multipleAssignment={false}
      alreadyAssignedUserTooltip={Strings.tooltipMessages.alreadyAssigned.userToGroup.oneUser}
      showRoleFilter
      search={props.search}
      onSearchChanged={onSearchMembersChanged}
      setReloadListItems={createReloadListItems}
      skipNotifyStep
    />
  );
};

const getUsersAssignments = (items: GroupAvailableUsers[]) => ({
  items: flatMap(items, (item) =>
    map(item.assignedGroupIds, (id) => ({
      userId: item.id,
      groupId: id,
    })),
  ),
  comparer: (assignment: { userId: number; groupId?: number }, id: number) => assignment.groupId === id,
});

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  selectedIds: state.people.addGroup.groupMembers.assign.list.selectedIds,
  items: state.people.addGroup.groupMembers.assign.list.items,
  search: state.people.addGroup.groupMembers.assign.filter.search,
  itemsCount: state.people.addGroup.groupMembers.assign.list.itemsCount,
  isLoading: state.people.addGroup.groupMembers.assign.list.isLoading,
  isShouldShowModalLoading: state.licensing.licensingModal.isLoading,
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchGroupUsersAssignmentAction: bindActionCreators(fetchGroupUsersAssignment, dispatch),
  addUsersToGroupsAction: bindActionCreators(addUsersToGroups, dispatch),
  shouldShowLicensingModalAction: bindAction(shouldShowModalForGroupMembership, dispatch),
  selectedItemsAction: bindAction(selectedItemsChanged, dispatch),
  resetSelectedItemsAction: bindAction(resetSelectedItems, dispatch),
  setSearchAction: bindAction(setSearch, dispatch),
  resetFilterAction: bindAction(resetFilter, dispatch),
  resetLicensingModalAction: bindAction(reset, dispatch),
  setActiveModalAction: bindAction(setActiveModal, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(UsersAssignmentModalContainer);
export default ConnectedComponent;
